import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { packagesAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Users, AlertCircle, User } from 'lucide-react';

const CheckoutStep1 = () => {
  const { packageId, dateId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [packageData, setPackageData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modalidade COM/SEM aéreo (querystring ?transport=with_air|without_air)
  const transportMode = searchParams.get('transport') === 'without_air' ? 'without_air' : 'with_air';

  // Preço efetivo considerando modalidade
  const effectiveBasePrice = useMemo(() => {
    if (!packageData) return 0;
    const base = parseFloat(packageData.price) || 0;
    const airCost = parseFloat(packageData.air_cost) || 0;
    const isAerial = ['nacional-aereo','internacional-aereo'].includes(packageData.category);
    if (isAerial && airCost > 0 && transportMode === 'without_air') {
      return Math.max(0, base - airCost);
    }
    return base;
  }, [packageData, transportMode]);
  
  // Seleções do usuário
  const [quantity, setQuantity] = useState(1);
  // Array de tipos de acomodação para cada pessoa
  const [accommodations, setAccommodations] = useState(['compartilhado']);

  useEffect(() => {
    loadPackageData();
  }, [packageId, dateId]);

  const loadPackageData = async () => {
    try {
      setLoading(true);
      const data = await packagesAPI.getById(packageId);
      setPackageData(data);
      
      // Encontrar a data selecionada
      const date = data.dates?.find(d => d.id === dateId);
      setSelectedDate(date);
    } catch (error) {
      console.error('Erro ao carregar pacote:', error);
    } finally {
      setLoading(false);
    }
  };

  // Verifica se é passeio de 1 dia (não tem acomodação)
  const isPasseio1Dia = packageData?.category === 'passeio-1-dia';

  // Opções de quarto padrão (sem ícones e descrições)
  const defaultRoomTypes = [
    {
      id: 'compartilhado',
      name: 'Quarto Compartilhado',
      priceAdjustment: 0
    },
    {
      id: 'casal',
      name: 'Quarto de Casal',
      priceAdjustment: packageData?.room_surcharges?.casal || 500
    },
    {
      id: 'privativo',
      name: 'Quarto Privativo',
      priceAdjustment: packageData?.room_surcharges?.individual || 800
    }
  ];

  // Filtra apenas as opções de acomodação habilitadas no pacote
  const roomTypes = useMemo(() => {
    const accommodationOptions = packageData?.accommodation_options;
    
    // Se o pacote não tem accommodation_options definidas, mostra todas
    if (!accommodationOptions || accommodationOptions.length === 0) {
      return defaultRoomTypes;
    }
    
    // Filtra para mostrar apenas as opções habilitadas
    return defaultRoomTypes.filter(room => {
      const option = accommodationOptions.find(opt => opt.type === room.id);
      // Se não encontrou a opção no pacote, não mostra (exceto compartilhado que sempre aparece)
      if (!option) {
        return room.id === 'compartilhado';
      }
      return option.enabled;
    }).map(room => {
      // Atualiza o nome e preço com os valores do pacote se existir
      const option = accommodationOptions.find(opt => opt.type === room.id);
      if (option) {
        return { 
          ...room, 
          // Usa o label personalizado se existir, senão usa o padrão
          name: option.label && option.label.trim() ? option.label : room.name,
          priceAdjustment: option.additional_price !== undefined ? option.additional_price : room.priceAdjustment 
        };
      }
      return room;
    });
  }, [packageData]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Atualiza array de acomodações quando a quantidade muda
  const handleQuantityChange = (newQty) => {
    const newAccommodations = [...accommodations];
    
    // Adiciona slots se aumentou a quantidade
    while (newAccommodations.length < newQty) {
      newAccommodations.push('compartilhado');
    }
    // Remove slots se diminuiu a quantidade
    while (newAccommodations.length > newQty) {
      newAccommodations.pop();
    }
    
    setQuantity(newQty);
    setAccommodations(newAccommodations);
  };

  // Atualiza acomodação de uma pessoa específica
  const updateAccommodation = (index, roomType) => {
    const newAccommodations = [...accommodations];
    newAccommodations[index] = roomType;
    setAccommodations(newAccommodations);
  };

  const calculateTotal = () => {
    if (!packageData) return 0;
    
    // Passeio de 1 dia não tem adicional de quarto
    if (isPasseio1Dia) {
      return effectiveBasePrice * quantity;
    }
    
    // Calcula o total baseado na acomodação de CADA pessoa
    let total = 0;
    accommodations.forEach(accommodation => {
      const roomPrice = roomTypes.find(r => r.id === accommodation)?.priceAdjustment || 0;
      total += effectiveBasePrice + roomPrice;
    });
    
    return total;
  };

  // Para o resumo - conta quantas pessoas em cada tipo de acomodação
  const getAccommodationSummary = () => {
    const summary = {};
    accommodations.forEach(acc => {
      summary[acc] = (summary[acc] || 0) + 1;
    });
    return summary;
  };

  const handleContinue = () => {
    // Salvar dados no sessionStorage
    const bookingData = {
      packageId: packageData.id,
      packageName: packageData.name,
      packageCategory: packageData.category,
      requiresPassport: packageData.requires_passport || false,
      dateId: selectedDate.id,
      selectedDate: selectedDate.date,
      startDate: selectedDate.start_date,
      quantity,
      roomType: isPasseio1Dia ? null : accommodations[0], // Para compatibilidade
      accommodations: isPasseio1Dia ? null : accommodations, // Array completo
      basePrice: effectiveBasePrice,
      packageOriginalPrice: packageData.price,
      airCost: parseFloat(packageData.air_cost) || 0,
      transportMode,
      totalPrice: calculateTotal()
    };
    
    sessionStorage.setItem('bookingData', JSON.stringify(bookingData));
    navigate('/checkout/dados-pessoais');
  };

  const isAvailable = selectedDate?.spots_available >= quantity;
  const isSoldOut = !selectedDate?.spots_available || selectedDate?.spots_available === 0 || !!selectedDate?.sales_closed;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700] mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando informações...</p>
        </div>
      </div>
    );
  }

  if (isSoldOut) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-red-200">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 data-testid="checkout-closed-title" className="text-2xl font-bold text-gray-900 mb-2">Vendas Encerradas</h2>
              <p className="text-gray-600 mb-6">
                {selectedDate?.sales_closed ? 'As vendas para esta data já foram encerradas.' : 'Infelizmente não há mais vagas disponíveis para esta data.'}
              </p>
              <Button
                onClick={() => navigate(`/pacote/${packageId}`)}
                className="bg-[#FFD700] hover:bg-[#FFC700] text-gray-900"
              >
                Escolher Outra Data
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6 md:py-12">
        {/* Progress Steps */}
        <div className="mb-8 overflow-x-auto">
          <div className="flex items-center justify-center min-w-fit mx-auto">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-[#FFD700] text-gray-900 rounded-full font-semibold">
                1
              </div>
              <span className="ml-2 text-sm font-medium text-gray-900 whitespace-nowrap">Quantidade</span>
            </div>
            <div className="w-12 md:w-20 h-0.5 bg-gray-300 mx-2 md:mx-4"></div>
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-600 rounded-full font-semibold">
                2
              </div>
              <span className="ml-2 text-sm text-gray-600 whitespace-nowrap">Dados</span>
            </div>
            <div className="w-12 md:w-20 h-0.5 bg-gray-300 mx-2 md:mx-4"></div>
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 bg-gray-300 text-gray-600 rounded-full font-semibold">
                3
              </div>
              <span className="ml-2 text-sm text-gray-600 whitespace-nowrap">Pagamento</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 md:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-4 md:p-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{packageData?.name}</h2>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-gray-600 mb-6">
                  <span className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 mr-1" />
                    {selectedDate?.date}
                  </span>
                  <Badge variant="outline">
                    {selectedDate?.spots_available} vagas disponíveis
                  </Badge>
                </div>

                {/* Seleção de Quantidade */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Quantidade de Pessoas</h3>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      -
                    </Button>
                    <span className="text-2xl font-bold w-12 text-center">{quantity}</span>
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleQuantityChange(Math.min(selectedDate?.spots_available || 10, quantity + 1))}
                      disabled={quantity >= (selectedDate?.spots_available || 10)}
                    >
                      +
                    </Button>
                  </div>
                </div>

                {/* Seleção de Acomodação */}
                {!isPasseio1Dia && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      Tipo de Acomodação
                      {quantity > 1 && <span className="text-sm font-normal text-gray-500 ml-2">(selecione para cada pessoa)</span>}
                    </h3>
                    
                    {/* Bloco Informativo sobre Quartos */}
                    {selectedDate?.spots_available > 0 && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                          <span className="mr-2">💡</span>
                          Informações Importantes sobre sua Reserva:
                        </p>
                        <ul className="text-sm text-gray-600 space-y-2">
                          <li><span className="font-medium">Quarto Compartilhado:</span> Prepare-se para fazer amigos! Você dividirá o espaço com 3 a 4 pessoas.</li>
                          <li><span className="font-medium">Quarto de Casal:</span> Se você vem acompanhado(a), ambas as pessoas devem selecionar esta opção (mesmo que comprem em pedidos separados).</li>
                          <li><span className="font-medium">Quarto Privativo:</span> Exclusivo para apenas 1 pessoa. É o seu cantinho particular!</li>
                        </ul>
                      </div>
                    )}
                    
                    {quantity === 1 ? (
                      // Uma pessoa: mostra cards como antes
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {roomTypes.map((room) => (
                          <div
                            key={room.id}
                            onClick={() => updateAccommodation(0, room.id)}
                            className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                              accommodations[0] === room.id
                                ? 'border-[#FFD700] bg-yellow-50'
                                : 'border-gray-200 hover:border-[#FFD700]'
                            }`}
                          >
                            <div className="text-center">
                              <h4 className="font-semibold text-gray-900 mb-2">{room.name}</h4>
                              {room.priceAdjustment > 0 && (
                                <p className="text-sm font-bold text-[#003366]">
                                  + {formatPrice(room.priceAdjustment)}
                                </p>
                              )}
                            </div>
                            {accommodations[0] === room.id && (
                              <div className="absolute top-2 right-2">
                                <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      // Múltiplas pessoas: mostra tabela compacta para mobile
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-gray-700">Pessoa</th>
                              <th className="px-2 md:px-4 py-2 md:py-3 text-left text-xs md:text-sm font-semibold text-gray-700">Acomodação</th>
                              <th className="px-2 md:px-4 py-2 md:py-3 text-right text-xs md:text-sm font-semibold text-gray-700">+</th>
                            </tr>
                          </thead>
                          <tbody>
                            {accommodations.map((acc, index) => {
                              const selectedRoom = roomTypes.find(r => r.id === acc);
                              const ordinal = index === 0 ? '1ª' : index === 1 ? '2ª' : index === 2 ? '3ª' : `${index + 1}ª`;
                              return (
                                <tr key={index} className="border-t hover:bg-gray-50">
                                  <td className="px-2 md:px-4 py-2 md:py-3">
                                    <div className="flex items-center gap-1 md:gap-2">
                                      <div className="w-7 h-7 md:w-8 md:h-8 bg-[#003366] text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                                        {ordinal}
                                      </div>
                                      <span className="text-xs md:text-sm text-gray-700 hidden sm:inline">Pessoa</span>
                                    </div>
                                  </td>
                                  <td className="px-2 md:px-4 py-2 md:py-3">
                                    <Select value={acc} onValueChange={(value) => updateAccommodation(index, value)}>
                                      <SelectTrigger className="w-full text-xs md:text-sm">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {roomTypes.map((room) => (
                                          <SelectItem key={room.id} value={room.id}>
                                            <div className="flex items-center gap-2">
                                              <span className="text-xs md:text-sm">{room.name}</span>
                                              {room.priceAdjustment > 0 && (
                                                <span className="text-xs text-gray-500">(+{formatPrice(room.priceAdjustment)})</span>
                                              )}
                                            </div>
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </td>
                                  <td className="px-2 md:px-4 py-2 md:py-3 text-right">
                                    {selectedRoom?.priceAdjustment > 0 ? (
                                      <span className="font-medium text-[#003366] text-xs md:text-sm">
                                        +{formatPrice(selectedRoom.priceAdjustment)}
                                      </span>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Botão Continuar */}
                <Button
                  onClick={handleContinue}
                  disabled={!isAvailable}
                  className="w-full bg-[#FFD700] hover:bg-[#FFC700] text-gray-900 font-semibold py-4 md:py-6 text-sm md:text-lg min-h-[56px] whitespace-normal"
                >
                  Continuar para Dados Pessoais
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Resumo Lateral */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-4">Resumo da Reserva</h3>
                
                <div className="space-y-3 mb-6">
                  {/* Aviso da modalidade escolhida */}
                  {(() => {
                    const airCost = parseFloat(packageData?.air_cost) || 0;
                    const isAerial = ['nacional-aereo','internacional-aereo'].includes(packageData?.category);
                    if (!isAerial || airCost <= 0) return null;
                    return (
                      <div className={`text-xs px-3 py-2 rounded ${transportMode === 'without_air' ? 'bg-amber-50 border border-amber-200 text-amber-900' : 'bg-blue-50 border border-blue-200 text-blue-900'}`} data-testid="checkout-transport-mode">
                        Modalidade: <strong>{transportMode === 'without_air' ? 'SEM AÉREO' : 'COM AÉREO'}</strong>
                        {transportMode === 'without_air' && (
                          <span className="block mt-0.5 text-[10px]">Preço reduzido em {formatPrice(airCost)} (você é responsável pela sua passagem).</span>
                        )}
                      </div>
                    );
                  })()}

                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Valor por pessoa:</span>
                    <div className="text-right">
                      {packageData?.discount_percentage > 0 && packageData?.original_price ? (
                        <>
                          <span className="text-gray-400 line-through text-xs mr-2">{formatPrice(transportMode === 'without_air' && parseFloat(packageData.air_cost) > 0 ? Math.max(0, packageData.original_price - parseFloat(packageData.air_cost)) : packageData.original_price)}</span>
                          <span className="font-semibold text-green-600">{formatPrice(effectiveBasePrice)}</span>
                          <span className="block text-xs text-green-600">-{packageData.discount_percentage}% OFF</span>
                        </>
                      ) : (
                        <span className="font-semibold" data-testid="checkout-per-person-price">{formatPrice(effectiveBasePrice)}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Quantidade:</span>
                    <span className="font-semibold">{quantity} pessoa(s)</span>
                  </div>
                  
                  {/* Detalhamento de Acomodações */}
                  {!isPasseio1Dia && quantity > 1 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-gray-500 mb-2">Acomodações selecionadas:</p>
                      {Object.entries(getAccommodationSummary()).map(([accType, count]) => {
                        const room = roomTypes.find(r => r.id === accType);
                        return (
                          <div key={accType} className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              {room?.name} ({count}x):
                            </span>
                            <span className="font-semibold">
                              {room?.priceAdjustment > 0 
                                ? `+${formatPrice(room.priceAdjustment * count)}`
                                : 'Incluso'
                              }
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {!isPasseio1Dia && quantity === 1 && accommodations[0] !== 'compartilhado' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Acomodação:</span>
                      <span className="font-semibold">
                        + {formatPrice(roomTypes.find(r => r.id === accommodations[0])?.priceAdjustment || 0)}
                      </span>
                    </div>
                  )}
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between">
                      <span className="font-bold text-gray-900">Total:</span>
                      <span className="font-bold text-2xl text-[#003366]">
                        {formatPrice(calculateTotal())}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 text-sm text-gray-700">
                  <p className="font-semibold mb-2">✓ Incluído no pacote:</p>
                  <ul className="space-y-1 text-xs">
                    {packageData?.included?.map((item, index) => (
                      <li key={index}>• {item}</li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutStep1;
